import { useEffect, useRef, useState } from 'react';
import lockIcon from '../../../../assets/images/tour-access-lock.svg';
import lockFailedIcon from '../../../../assets/images/tour-access-lock-failed.svg';
import lockOpenIcon from '../../../../assets/images/tour-access-lock-open.svg';
import alarmOnIcon from '../../../../assets/images/tour-access-alarm-on.svg';
import alarmOffIcon from '../../../../assets/images/tour-access-alarm-off.svg';
import codeIcon from '../../../../assets/images/tour-access-code.svg';
import codeSuccessIcon from '../../../../assets/images/tour-access-code-success.svg';
import './TourAccessLoader.css';

const DEFAULT_TOTAL_MS = 10000;
const DELAYED_TOTAL_MS = 30000;
const STAGE_SUCCESS_MS = 1200;

type TourAccessLoaderProps = {
  runKey: number;
  onDone: () => void;
  shouldDelayAnimation: boolean;
  isRequestLoading: boolean;
  isUnlocking: boolean;
};

const useTicker = (runKey: number, totalMs: number) => {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(performance.now());
  const rafRef = useRef<number>();

  useEffect(() => {
    startRef.current = performance.now();
    setElapsed(0);

    const tick = (time: number) => {
      const nextElapsed = Math.min(totalMs, time - startRef.current);
      setElapsed(nextElapsed);

      if (nextElapsed < totalMs) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [runKey, totalMs]);

  return elapsed;
};

const TourAccessLoader = ({
  runKey,
  onDone,
  shouldDelayAnimation,
  isRequestLoading,
  isUnlocking,
}: TourAccessLoaderProps) => {
  const totalMs = shouldDelayAnimation ? DELAYED_TOTAL_MS : DEFAULT_TOTAL_MS;
  const disarmStageEnd = totalMs * 0.3;
  const codeStageEnd = totalMs * 0.6;
  const elapsed = useTicker(runKey, totalMs);
  const stage = elapsed < disarmStageEnd ? 1 : elapsed < codeStageEnd ? 2 : 3;
  const secondsLeft = Math.ceil(Math.max(0, totalMs - elapsed) / 1000);
  const isMinimumDelayComplete = elapsed >= totalMs;
  const isWaitingForRequest = isMinimumDelayComplete && isRequestLoading;

  useEffect(() => {
    if (isMinimumDelayComplete && !isRequestLoading) {
      onDone();
    }
  }, [isMinimumDelayComplete, isRequestLoading, onDone]);

  const titles = {
    1: 'Disarming the alarm',
    2: 'Adding your access code',
    3: 'Unlocking the door',
  };
  const descriptions = {
    1: "We're sending the disarm signal to the home's alarm system.",
    2: "We're securely sending your temporary visitor code to the lock.",
    3: 'Your backup code is ready while we request the lock to open.',
  };

  const radius = 96;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(1, elapsed / totalMs);

  const renderStageIcon = () => {
    if (stage === 1) {
      const isDisarmed = elapsed >= disarmStageEnd - STAGE_SUCCESS_MS;
      return (
        <div className={`tour-access-stage-icon alarm swap${isDisarmed ? ' off' : ''}`}>
          <img src={isDisarmed ? alarmOffIcon : alarmOnIcon} alt="alarm" />
          {!isDisarmed && (
            <>
              <span className="tour-access-pulse-ring"></span>
              <span className="tour-access-pulse-ring p2"></span>
            </>
          )}
        </div>
      );
    }

    if (stage === 2) {
      const isCodeAdded = elapsed >= codeStageEnd - STAGE_SUCCESS_MS;
      return (
        <div className={`tour-access-stage-icon code swap${isCodeAdded ? ' success' : ''}`}>
          <img src={isCodeAdded ? codeSuccessIcon : codeIcon} alt="access code" />
        </div>
      );
    }

    const shouldShowUnlockResult =
      !isRequestLoading && elapsed >= totalMs - STAGE_SUCCESS_MS;
    const isUnlocked = shouldShowUnlockResult && isUnlocking;
    const didUnlockFail = shouldShowUnlockResult && !isUnlocking;

    return (
      <div
        className={`tour-access-stage-icon lock swap${
          isUnlocked ? ' success' : didUnlockFail ? ' failure' : ''
        }`}
      >
        <img
          src={isUnlocked ? lockOpenIcon : didUnlockFail ? lockFailedIcon : lockIcon}
          alt="door lock"
        />
        {!shouldShowUnlockResult && <div className="tour-access-signal-arc"></div>}
      </div>
    );
  };

  return (
    <div className="tour-access-loader">
      <div className="tour-access-title">Access Information</div>
      <div className="tour-access-loader-subtitle">
        Please wait while we prepare the property for your tour.
      </div>
      {shouldDelayAnimation && (
        <div className="tour-access-loader-subtitle">
          Your access code should be ready within 30 seconds to one minute.
        </div>
      )}

      <div className="tour-access-ring-wrap">
        <svg className="tour-access-ring-svg" viewBox="0 0 220 220">
          <defs>
            <linearGradient id="tourAccessRingGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#F08A3E" />
              <stop offset="100%" stopColor="#D55B17" />
            </linearGradient>
          </defs>
          <circle className="tour-access-ring-track" cx="110" cy="110" r={radius} strokeWidth="12" fill="none" />
          <circle
            className="tour-access-ring-progress"
            cx="110"
            cy="110"
            r={radius}
            strokeWidth="12"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - progress)}
          />
        </svg>

        <div className="tour-access-ring-center">
          {isWaitingForRequest ? (
            <>
              <div className="tour-access-count-num">...</div>
              <div className="tour-access-count-label">Unlocking</div>
            </>
          ) : (
            <>
              <div className="tour-access-count-num">{secondsLeft}</div>
              <div className="tour-access-count-label">Seconds Remaining</div>
            </>
          )}
        </div>

        {renderStageIcon()}
      </div>

      <div className="tour-access-stage-title">{titles[stage]}</div>
      <div className="tour-access-stage-desc">{descriptions[stage]}</div>

      <div className="tour-access-steps">
        <div className={`tour-access-step ${stage > 1 ? 'done' : 'active'}`}>
          <div className="bubble">{stage > 1 ? '✓' : '1'}</div>
          <span>Disarm alarm</span>
        </div>
        <div className={`tour-access-step-line${stage > 1 ? ' done' : ''}`}></div>
        <div className={`tour-access-step ${stage > 2 ? 'done' : stage === 2 ? 'active' : ''}`}>
          <div className="bubble">{stage > 2 ? '✓' : '2'}</div>
          <span>Add code</span>
        </div>
        <div className={`tour-access-step-line${stage > 2 ? ' done' : ''}`}></div>
        <div className={`tour-access-step ${stage === 3 ? 'active' : ''}`}>
          <div className="bubble">3</div>
          <span>Unlock door</span>
        </div>
      </div>

    </div>
  );
};

export default TourAccessLoader;
